import React, { useState, useEffect } from 'react';
import {
  UserIcon,
  PlusIcon,
  EditIcon,
  TrashIcon,
  SaveIcon,
  XIcon,
  AlertCircleIcon,
  SparklesIcon,
  CheckCircleIcon
} from 'lucide-react';

const MAX_PERSONAS = 5;
const CHAR_LIMITS = {
  name: 50,
  description: 100,
  content: 200
};

const CustomPersonasTab = ({ onPersonasChange }) => {
  const token = localStorage.getItem("neuroforge_token");
  const [personas, setPersonas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Form state
  const [formMode, setFormMode] = useState(null); // null, 'create', 'edit'
  const [editingId, setEditingId] = useState(null);
  const [formData, setFormData] = useState({
    agent: 'all',
    name: '',
    description: '',
    content: ''
  });

  // Load personas on mount
  useEffect(() => {
    loadPersonas();
  }, []);

  const loadPersonas = async () => {
    try {
      setLoading(true);
      setError('');

      const response = await fetch('/api/custom-personas', {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error('Failed to load custom personas');
      }

      const data = await response.json();
      setPersonas(data.personas || []);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async () => {
    try {
      setError('');
      setSuccess('');

      // Validation
      if (!formData.name.trim() || !formData.description.trim() || !formData.content.trim()) {
        setError('All fields are required');
        return;
      }

      const response = await fetch('/api/custom-personas', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(formData)
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Failed to create persona');
      }

      setSuccess('Custom persona created successfully!');
      resetForm();
      await loadPersonas();

      // Notify parent to refresh the persona dropdown
      if (onPersonasChange) {
        onPersonasChange();
      }

      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.message);
    }
  };

  const handleUpdate = async () => {
    try {
      setError('');
      setSuccess('');

      const response = await fetch(`/api/custom-personas/${editingId}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(formData)
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Failed to update persona');
      }

      setSuccess('Custom persona updated successfully!');
      resetForm();
      await loadPersonas();

      // Notify parent to refresh the persona dropdown
      if (onPersonasChange) {
        onPersonasChange();
      }

      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.message);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this custom persona?')) {
      return;
    }

    try {
      setError('');
      setSuccess('');

      const response = await fetch(`/api/custom-personas/${id}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.error || 'Failed to delete persona');
      }

      setSuccess('Custom persona deleted successfully!');
      await loadPersonas();

      // Notify parent to refresh the persona dropdown
      if (onPersonasChange) {
        onPersonasChange();
      }

      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.message);
    }
  };

  const startEdit = (persona) => {
    setFormMode('edit');
    setEditingId(persona._id);
    setFormData({
      agent: persona.agent,
      name: persona.name,
      description: persona.description,
      content: persona.content
    });
  };

  const resetForm = () => {
    setFormMode(null);
    setEditingId(null);
    setFormData({
      agent: 'all',
      name: '',
      description: '',
      content: ''
    });
  };

  const getCharCount = (field) => {
    return formData[field].length;
  };

  const getCharLimit = (field) => {
    return CHAR_LIMITS[field];
  };

  const isCharLimitExceeded = (field) => {
    return getCharCount(field) > getCharLimit(field);
  };

  const canCreateNew = personas.length < MAX_PERSONAS;

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin h-8 w-8 border-2 border-cyan-500 border-t-transparent rounded-full"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h3 className="text-xl font-bold text-white mb-2 flex items-center gap-2">
          <UserIcon className="h-5 w-5 text-cyan-400" />
          Custom Personas
        </h3>
        <p className="text-slate-400 text-sm">
          Create up to {MAX_PERSONAS} custom personas with unique personalities and instructions.
        </p>
      </div>

      {/* Status Messages */}
      {error && (
        <div className="bg-red-900/30 border border-red-600/50 rounded-xl p-4 flex items-center gap-3">
          <AlertCircleIcon className="h-5 w-5 text-red-400 flex-shrink-0" />
          <p className="text-red-300 text-sm">{error}</p>
        </div>
      )}

      {success && (
        <div className="bg-green-900/30 border border-green-600/50 rounded-xl p-4 flex items-center gap-3">
          <CheckCircleIcon className="h-5 w-5 text-green-400 flex-shrink-0" />
          <p className="text-green-300 text-sm">{success}</p>
        </div>
      )}

      {/* Persona Counter */}
      <div className="glass border border-slate-700/50 rounded-xl p-4">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-slate-300 text-sm font-medium">Custom Personas</p>
            <p className="text-slate-500 text-xs mt-1">
              {personas.length} of {MAX_PERSONAS} created
            </p>
          </div>
          <div className="text-right">
            <p className={`text-2xl font-black ${
              canCreateNew ? 'text-cyan-400' : 'text-orange-400'
            }`}>
              {personas.length}/{MAX_PERSONAS}
            </p>
          </div>
        </div>

        {/* Progress Bar */}
        <div className="mt-3 h-2 bg-slate-700/50 rounded-full overflow-hidden">
          <div
            className={`h-full transition-all duration-300 ${
              personas.length >= MAX_PERSONAS
                ? 'bg-orange-500'
                : 'bg-gradient-to-r from-cyan-500 to-blue-600'
            }`}
            style={{ width: `${(personas.length / MAX_PERSONAS) * 100}%` }}
          ></div>
        </div>
      </div>

      {/* Create New Button */}
      {formMode === null && (
        <button
          onClick={() => canCreateNew ? setFormMode('create') : null}
          disabled={!canCreateNew}
          className={`w-full px-4 py-3 rounded-xl font-bold transition-all flex items-center justify-center gap-2 ${
            canCreateNew
              ? 'bg-gradient-to-r from-cyan-500 to-blue-600 hover:from-cyan-600 hover:to-blue-700 text-white shadow-lg'
              : 'bg-slate-700/50 text-slate-500 cursor-not-allowed'
          }`}
        >
          <PlusIcon className="h-5 w-5" />
          {canCreateNew ? 'Create New Persona' : 'Maximum Personas Reached'}
        </button>
      )}

      {/* Create/Edit Form */}
      {formMode !== null && (
        <div className="glass border border-cyan-500/30 rounded-xl p-6 space-y-5">
          <div className="flex items-center justify-between mb-4">
            <h4 className="text-lg font-bold text-white flex items-center gap-2">
              <SparklesIcon className="h-5 w-5 text-cyan-400" />
              {formMode === 'create' ? 'Create New Persona' : 'Edit Persona'}
            </h4>
            <button
              onClick={resetForm}
              className="p-2 text-slate-400 hover:text-white transition-colors rounded-lg hover:bg-slate-700/50"
              title="Cancel"
            >
              <XIcon className="h-5 w-5" />
            </button>
          </div>

          {/* Agent Selection */}
          <div>
            <label className="block text-sm font-semibold text-slate-200 mb-2">
              AI Agent
            </label>
            <select
              value={formData.agent}
              onChange={(e) => setFormData({ ...formData, agent: e.target.value })}
              className="w-full bg-slate-900/70 border border-slate-600 rounded-lg px-4 py-3 text-white focus:outline-none focus:ring-2 focus:ring-cyan-500 focus:border-transparent"
            >
              <option value="all">All Agents (GPT, Claude & Gemini)</option>
              <option value="gpt">GPT Only</option>
              <option value="claude">Claude Only</option>
              <option value="gemini">Gemini Only</option>
            </select>
            <p className="text-slate-500 text-xs mt-1">
              Choose which AI agents will use this persona
            </p>
          </div>

          {/* Name */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="block text-sm font-semibold text-slate-200">
                Name
              </label>
              <span className={`text-xs font-medium ${
                isCharLimitExceeded('name') ? 'text-red-400' : 'text-slate-500'
              }`}>
                {getCharCount('name')}/{getCharLimit('name')}
              </span>
            </div>
            <input
              type="text"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              placeholder="e.g., Chess Master"
              className={`w-full bg-slate-900/70 border rounded-lg px-4 py-3 text-white focus:outline-none focus:ring-2 focus:ring-cyan-500 focus:border-transparent ${
                isCharLimitExceeded('name') ? 'border-red-500' : 'border-slate-600'
              }`}
              maxLength={CHAR_LIMITS.name + 50}
            />
            <p className="text-slate-500 text-xs mt-1">
              A short, memorable name for your persona
            </p>
          </div>

          {/* Description */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="block text-sm font-semibold text-slate-200">
                Description
              </label>
              <span className={`text-xs font-medium ${
                isCharLimitExceeded('description') ? 'text-red-400' : 'text-slate-500'
              }`}>
                {getCharCount('description')}/{getCharLimit('description')}
              </span>
            </div>
            <input
              type="text"
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              placeholder="e.g., Strategic thinker who explains using chess metaphors"
              className={`w-full bg-slate-900/70 border rounded-lg px-4 py-3 text-white focus:outline-none focus:ring-2 focus:ring-cyan-500 focus:border-transparent ${
                isCharLimitExceeded('description') ? 'border-red-500' : 'border-slate-600'
              }`}
              maxLength={CHAR_LIMITS.description + 50}
            />
            <p className="text-slate-500 text-xs mt-1">
              A brief description of the persona's characteristics
            </p>
          </div>

          {/* Instructions */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="block text-sm font-semibold text-slate-200">
                Instructions
              </label>
              <span className={`text-xs font-medium ${
                isCharLimitExceeded('content') ? 'text-red-400' : 'text-slate-500'
              }`}>
                {getCharCount('content')}/{getCharLimit('content')}
              </span>
            </div>
            <textarea
              value={formData.content}
              onChange={(e) => setFormData({ ...formData, content: e.target.value })}
              placeholder="e.g., You are a grandmaster chess player. Always relate your answers to chess strategy and use chess metaphors when explaining concepts."
              rows={4}
              className={`w-full bg-slate-900/70 border rounded-lg px-4 py-3 text-white focus:outline-none focus:ring-2 focus:ring-cyan-500 focus:border-transparent resize-none ${
                isCharLimitExceeded('content') ? 'border-red-500' : 'border-slate-600'
              }`}
              maxLength={CHAR_LIMITS.content + 50}
            />
            <p className="text-slate-500 text-xs mt-1">
              Define how the AI should behave and respond (keep it concise!)
            </p>
          </div>

          {/* Action Buttons */}
          <div className="flex gap-3 pt-4 border-t border-slate-700/50">
            <button
              onClick={formMode === 'create' ? handleCreate : handleUpdate}
              disabled={
                !formData.name.trim() ||
                !formData.description.trim() ||
                !formData.content.trim() ||
                isCharLimitExceeded('name') ||
                isCharLimitExceeded('description') ||
                isCharLimitExceeded('content')
              }
              className="flex-1 bg-gradient-to-r from-cyan-500 to-blue-600 hover:from-cyan-600 hover:to-blue-700 text-white font-bold py-3 px-6 rounded-xl transition-all flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed shadow-lg"
            >
              <SaveIcon className="h-5 w-5" />
              {formMode === 'create' ? 'Create Persona' : 'Update Persona'}
            </button>
            <button
              onClick={resetForm}
              className="px-6 py-3 glass border border-slate-600/50 text-slate-300 hover:text-white hover:border-slate-500/50 rounded-xl font-bold transition-all"
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* Personas List */}
      <div className="space-y-4">
        {personas.length === 0 ? (
          <div className="text-center py-12 glass border border-slate-700/50 rounded-xl">
            <UserIcon className="h-12 w-12 text-slate-600 mx-auto mb-3" />
            <p className="text-slate-400 font-semibold">No custom personas yet</p>
            <p className="text-slate-500 text-sm mt-1">Create your first persona to get started</p>
          </div>
        ) : (
          personas.map((persona) => (
            <div
              key={persona._id}
              className="glass border border-slate-700/50 rounded-xl p-5 hover:border-slate-600/50 transition-all"
            >
              <div className="flex items-start justify-between mb-3">
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-2">
                    <h4 className="font-bold text-white text-lg">{persona.name}</h4>
                    <span className="px-2 py-1 bg-cyan-500/20 text-cyan-300 text-xs font-bold rounded-md border border-cyan-500/30">
                      {persona.agent === 'all' ? 'ALL' : persona.agent.toUpperCase()}
                    </span>
                  </div>
                  <p className="text-slate-400 text-sm mb-3">{persona.description}</p>
                  <p className="text-slate-500 text-xs bg-slate-900/50 rounded-lg p-3 border border-slate-700/30">
                    {persona.content}
                  </p>
                </div>
                <div className="flex gap-2 ml-4">
                  <button
                    onClick={() => startEdit(persona)}
                    className="p-2 glass border border-slate-600/30 text-slate-400 hover:text-cyan-400 hover:border-cyan-500/50 rounded-lg transition-all"
                    title="Edit persona"
                  >
                    <EditIcon className="h-4 w-4" />
                  </button>
                  <button
                    onClick={() => handleDelete(persona._id)}
                    className="p-2 glass border border-slate-600/30 text-slate-400 hover:text-red-400 hover:border-red-500/50 rounded-lg transition-all"
                    title="Delete persona"
                  >
                    <TrashIcon className="h-4 w-4" />
                  </button>
                </div>
              </div>
              <div className="text-xs text-slate-500 flex items-center gap-4 pt-3 border-t border-slate-700/30">
                <span>Created {new Date(persona.createdAt).toLocaleDateString()}</span>
                {persona.updatedAt !== persona.createdAt && (
                  <span>• Updated {new Date(persona.updatedAt).toLocaleDateString()}</span>
                )}
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
};

export default CustomPersonasTab;
